import React, { useState, useEffect } from 'react';
import { Mail, Send, RefreshCw, Settings, CheckCircle, XCircle, Clock, AlertTriangle, Loader2, RotateCcw, Eye, X, Search, ChevronLeft, ChevronRight, FileText, Code, ToggleLeft, ToggleRight, Save } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { emailSettingsApi } from '../services/api';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 20;

const EmailSettingsPage = () => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  // Settings state
  const [settings, setSettings] = useState({
    sender_email: '',
    sender_name: '',
    reply_to_email: '',
    email_footer: '',
    resend_configured: false
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Email logs state
  const [emails, setEmails] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetailsLoading, setEmailDetailsLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [filterDomain, setFilterDomain] = useState(null);

  // Server-side pagination state
  const [hasMore, setHasMore] = useState(false);
  const [pageHistory, setPageHistory] = useState([]); // Stack of first_ids for previous pages
  const [currentFirstId, setCurrentFirstId] = useState(null);
  const [currentLastId, setCurrentLastId] = useState(null);

  // Search state (client-side filter on current page)
  const [searchQuery, setSearchQuery] = useState('');

  // Test email state
  const [sendingTest, setSendingTest] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('logs');

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sendingTestTemplate, setSendingTestTemplate] = useState(null);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  // Load email logs on mount (separate effect to avoid dependency issues)
  const initialLoadDone = React.useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadEmailLogs();
    }
  });

  // Load templates when tab switches
  useEffect(() => {
    if (activeTab === 'templates' && templates.length === 0) {
      loadTemplates();
    }
  }, [activeTab]);

  // Filter displayed emails when search changes (client-side on current page)
  const displayedEmails = searchQuery.trim()
    ? emails.filter(email => {
        const query = searchQuery.toLowerCase();
        const to = Array.isArray(email.to) ? email.to.join(', ') : email.to;
        return (
          to?.toLowerCase().includes(query) ||
          email.subject?.toLowerCase().includes(query) ||
          email.last_event?.toLowerCase().includes(query)
        );
      })
    : emails;

  const loadSettings = async () => {
    try {
      const response = await emailSettingsApi.getSettings();
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to load email settings:', err);
      toast.error('Failed to load email settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadEmailLogs = async (afterId = null, beforeId = null, isNextPage = false, isPrevPage = false) => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const response = await emailSettingsApi.getLogs(ITEMS_PER_PAGE, afterId, beforeId);
      const data = response.data;
      
      setEmails(data.emails || []);
      setHasMore(data.has_more || false);
      setCurrentFirstId(data.first_id);
      setCurrentLastId(data.last_id);
      setFilterDomain(data.filter_domain || null);
      
      // Update page history for navigation
      if (isNextPage && currentFirstId) {
        setPageHistory(prev => [...prev, currentFirstId]);
      } else if (isPrevPage) {
        setPageHistory(prev => prev.slice(0, -1));
      } else if (!afterId && !beforeId) {
        // Fresh load - reset history
        setPageHistory([]);
      }
    } catch (err) {
      console.error('Failed to load email logs:', err);
      const errorDetail = err.response?.data?.detail || '';
      
      if (err.response?.status === 403 || errorDetail.includes('restricted') || errorDetail.includes('Full Access')) {
        setLogsError({
          type: 'permissions',
          message: 'Your Resend API key has restricted permissions.',
          detail: 'To view email logs, you need a Full Access API key from resend.com/api-keys'
        });
      } else if (err.response?.status !== 400) {
        setLogsError({
          type: 'error',
          message: 'Failed to load email logs',
          detail: errorDetail
        });
      }
    } finally {
      setLogsLoading(false);
    }
  };

  const handleNextPage = () => {
    if (currentLastId && hasMore) {
      loadEmailLogs(currentLastId, null, true, false);
    }
  };

  const handlePrevPage = () => {
    if (pageHistory.length > 0) {
      const prevFirstId = pageHistory[pageHistory.length - 1];
      // Load page before the previous first item
      loadEmailLogs(null, prevFirstId, false, true);
    } else {
      // Go to first page
      loadEmailLogs();
    }
  };

  const handleRefresh = () => {
    loadEmailLogs();
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await emailSettingsApi.updateSettings({
        sender_email: settings.sender_email,
        sender_name: settings.sender_name,
        reply_to_email: settings.reply_to_email,
        email_footer: settings.email_footer
      });
      setSettings(response.data);
      setHasChanges(false);
      toast.success('Email settings saved');
      setShowSettingsModal(false);
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // ============ Template Functions ============
  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await emailSettingsApi.getTemplates();
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
      toast.error('Failed to load email templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditingTemplate({
      name: template.name,
      subject: template.subject,
      html_body: template.html_body,
      enabled: template.enabled
    });
  };

  const handleTemplateChange = (field, value) => {
    setEditingTemplate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate || !editingTemplate) return;
    
    setSavingTemplate(true);
    try {
      await emailSettingsApi.updateTemplate(selectedTemplate.template_id, editingTemplate);
      toast.success('Template saved successfully');
      
      // Update local state
      setTemplates(prev => prev.map(t => 
        t.template_id === selectedTemplate.template_id 
          ? { ...t, ...editingTemplate }
          : t
      ));
      setSelectedTemplate(prev => ({ ...prev, ...editingTemplate }));
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSendTestTemplate = async (templateId) => {
    setSendingTestTemplate(templateId);
    try {
      const response = await emailSettingsApi.sendTestTemplate(templateId);
      toast.success(response.data.message || 'Test email sent');
    } catch (err) {
      console.error('Failed to send test template:', err);
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestTemplate(null);
    }
  };

  const getTemplateIcon = (templateId) => {
    const icons = {
      subscription_alert: AlertTriangle,
      ai_complete: CheckCircle,
      research_complete: FileText,
      mention: Mail,
      feature_assigned: CheckCircle,
      feature_unassigned: XCircle,
      kanban_update: RefreshCw
    };
    return icons[templateId] || Mail;
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const response = await emailSettingsApi.sendTestEmail();
      toast.success(response.data.message);
      setTimeout(loadEmailLogs, 2000);
    } catch (err) {
      console.error('Failed to send test email:', err);
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleViewEmailDetails = async (emailId) => {
    setEmailDetailsLoading(true);
    try {
      const response = await emailSettingsApi.getEmailDetails(emailId);
      setSelectedEmail(response.data);
    } catch (err) {
      console.error('Failed to load email details:', err);
      toast.error('Failed to load email details');
    } finally {
      setEmailDetailsLoading(false);
    }
  };

  const handleResendEmail = async (emailId) => {
    setResendingId(emailId);
    try {
      const response = await emailSettingsApi.resendEmail(emailId);
      toast.success(response.data.message);
      setTimeout(loadEmailLogs, 2000);
    } catch (err) {
      console.error('Failed to resend email:', err);
      toast.error(err.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      delivered: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
      sent: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Send },
      opened: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', icon: Eye },
      clicked: { color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400', icon: CheckCircle },
      bounced: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
      complained: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', icon: AlertTriangle },
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={cn("flex items-center gap-1", config.color)}>
        <Icon className="w-3 h-3" />
        {status || 'unknown'}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className={cn("min-h-screen flex", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col min-h-screen">
        <Header />
        <main className={cn("flex-1 p-6 overflow-auto", colors.bgPrimary)}>
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className={cn("text-2xl font-bold flex items-center gap-2", colors.textPrimary)}>
                  <Mail className="w-6 h-6" />
                  Email Settings
                </h1>
                <p className={cn("text-sm mt-1", colors.textMuted)}>
                  Monitor email delivery and manage configuration
                </p>
              </div>
              <div className="flex items-center gap-2">
                {settings.resend_configured ? (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Resend Connected
                  </Badge>
                ) : (
                  <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Resend Not Configured
                  </Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSettingsModal(true)}
                  title="Email Configuration"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Tabs for Logs and Templates */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={cn("grid w-full grid-cols-2 mb-4", colors.bgTertiary)}>
                <TabsTrigger value="logs" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Logs
                </TabsTrigger>
                <TabsTrigger value="templates" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Templates
                </TabsTrigger>
              </TabsList>

              {/* Logs Tab Content */}
              <TabsContent value="logs" className="space-y-6">
                {/* Email Overview - Full Width */}
                <Card className={cn(colors.bgCard, colors.borderPrimary)}>
              <CardHeader className="pb-2">
                <CardTitle className={cn("flex items-center gap-2 text-lg", colors.textPrimary)}>
                  <Mail className="w-5 h-5" />
                  Email Overview
                </CardTitle>
                <CardDescription className={colors.textMuted}>
                  Recent email statistics
                </CardDescription>
              </CardHeader>
              <CardContent>
                {logsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                  </div>
                ) : logsError ? (
                  <div className={cn("text-center py-6", colors.textMuted)}>
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                    <p className="text-sm">Stats unavailable - API key needs Full Access</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className={cn("p-4 rounded-lg text-center", colors.bgTertiary)}>
                      <p className={cn("text-3xl font-bold", colors.textPrimary)}>{emails.length}</p>
                      <p className={cn("text-sm", colors.textMuted)}>Total Emails</p>
                    </div>
                    <div className={cn("p-4 rounded-lg text-center", colors.bgTertiary)}>
                      <p className="text-3xl font-bold text-green-500">
                        {emails.filter(e => e.last_event === 'delivered').length}
                      </p>
                      <p className={cn("text-sm", colors.textMuted)}>Delivered</p>
                    </div>
                    <div className={cn("p-4 rounded-lg text-center", colors.bgTertiary)}>
                      <p className="text-3xl font-bold text-red-500">
                        {emails.filter(e => e.last_event === 'bounced').length}
                      </p>
                      <p className={cn("text-sm", colors.textMuted)}>Bounced</p>
                    </div>
                    <div className={cn("p-4 rounded-lg text-center", colors.bgTertiary)}>
                      <p className="text-3xl font-bold text-purple-500">
                        {emails.filter(e => e.last_event === 'opened').length}
                      </p>
                      <p className={cn("text-sm", colors.textMuted)}>Opened</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Logs Table */}
            <Card className={cn(colors.bgCard, colors.borderPrimary)}>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
                      <Mail className="w-5 h-5" />
                      Email Logs
                    </CardTitle>
                    <CardDescription className={colors.textMuted}>
                      {filterDomain ? (
                        <>Showing emails from <span className="font-medium">@{filterDomain}</span></>
                      ) : (
                        'View sent emails and their delivery status'
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Search */}
                    <div className="relative">
                      <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search emails..."
                        className={cn("pl-9 w-64", colors.bgInput, colors.borderPrimary)}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={logsLoading}
                    >
                      <RefreshCw className={cn("w-4 h-4", logsLoading && "animate-spin")} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSendTestEmail}
                      disabled={sendingTest || !settings.resend_configured}
                    >
                      {sendingTest ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Send className="w-4 h-4 mr-1" />Test</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {logsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
                  </div>
                ) : logsError ? (
                  <div className={cn("text-center py-12", colors.textMuted)}>
                    {logsError.type === 'permissions' ? (
                      <>
                        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-yellow-500" />
                        <p className={cn("font-medium", colors.textPrimary)}>{logsError.message}</p>
                        <p className="text-sm mt-1">{logsError.detail}</p>
                        <a 
                          href="https://resend.com/api-keys" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-block mt-4 text-blue-500 hover:text-blue-600 underline text-sm"
                        >
                          Create Full Access API Key →
                        </a>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-12 h-12 mx-auto mb-3 text-red-500" />
                        <p className={cn("font-medium", colors.textPrimary)}>{logsError.message}</p>
                        {logsError.detail && <p className="text-sm mt-1">{logsError.detail}</p>}
                      </>
                    )}
                  </div>
                ) : displayedEmails.length === 0 ? (
                  <div className={cn("text-center py-12", colors.textMuted)}>
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{searchQuery ? 'No emails match your search on this page' : 'No emails sent yet'}</p>
                    {!searchQuery && <p className="text-sm mt-1">Send a test email to get started</p>}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className={cn("border-b", colors.borderPrimary)}>
                            <th className={cn("px-4 py-3 text-left text-sm font-medium", colors.textMuted)}>To</th>
                            <th className={cn("px-4 py-3 text-left text-sm font-medium", colors.textMuted)}>Subject</th>
                            <th className={cn("px-4 py-3 text-left text-sm font-medium", colors.textMuted)}>Status</th>
                            <th className={cn("px-4 py-3 text-left text-sm font-medium", colors.textMuted)}>Sent</th>
                            <th className={cn("px-4 py-3 text-right text-sm font-medium", colors.textMuted)}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedEmails.map((email) => (
                            <tr key={email.id} className={cn("border-b", colors.borderPrimary, "hover:bg-gray-50 dark:hover:bg-gray-800")}>
                              <td className={cn("px-4 py-3 text-sm", colors.textPrimary)}>
                                {Array.isArray(email.to) ? email.to.join(', ') : email.to}
                              </td>
                              <td className={cn("px-4 py-3 text-sm max-w-xs truncate", colors.textPrimary)}>
                                {email.subject || 'No subject'}
                              </td>
                              <td className="px-4 py-3">
                                {getStatusBadge(email.last_event)}
                              </td>
                              <td className={cn("px-4 py-3 text-sm", colors.textMuted)}>
                                {formatDate(email.created_at)}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleViewEmailDetails(email.id)}
                                    title="View details"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleResendEmail(email.id)}
                                    disabled={resendingId === email.id}
                                    title="Resend email"
                                  >
                                    {resendingId === email.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <RotateCcw className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {(hasMore || pageHistory.length > 0) && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className={cn("text-sm", colors.textMuted)}>
                          Showing {emails.length} emails{searchQuery && ` (${displayedEmails.length} match filter)`}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePrevPage}
                            disabled={pageHistory.length === 0 || logsLoading}
                          >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleNextPage}
                            disabled={!hasMore || logsLoading}
                          >
                            Next
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
              </TabsContent>

              {/* Templates Tab Content */}
              <TabsContent value="templates" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Templates List */}
                  <Card className={cn(colors.bgCard, colors.borderPrimary)}>
                    <CardHeader className="pb-2">
                      <CardTitle className={cn("flex items-center gap-2 text-lg", colors.textPrimary)}>
                        <FileText className="w-5 h-5" />
                        Email Templates
                      </CardTitle>
                      <CardDescription className={colors.textMuted}>
                        Click a template to edit
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {templatesLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {templates.map((template) => {
                            const TemplateIcon = getTemplateIcon(template.template_id);
                            const isSelected = selectedTemplate?.template_id === template.template_id;
                            
                            return (
                              <button
                                key={template.template_id}
                                onClick={() => handleSelectTemplate(template)}
                                className={cn(
                                  "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
                                  isSelected 
                                    ? "bg-violet-500/20 border border-violet-500/50" 
                                    : cn(colors.bgTertiary, "hover:bg-violet-500/10 border border-transparent"),
                                )}
                              >
                                <TemplateIcon className={cn("w-4 h-4 shrink-0", isSelected ? "text-violet-400" : colors.textMuted)} />
                                <div className="flex-1 min-w-0">
                                  <p className={cn("font-medium truncate", colors.textPrimary)}>
                                    {template.name}
                                  </p>
                                  <p className={cn("text-xs truncate", colors.textMuted)}>
                                    {template.template_id}
                                  </p>
                                </div>
                                {template.enabled ? (
                                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs">
                                    On
                                  </Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700/30 dark:text-gray-400 text-xs">
                                    Off
                                  </Badge>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Template Editor */}
                  <Card className={cn("lg:col-span-2", colors.bgCard, colors.borderPrimary)}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className={cn("flex items-center gap-2 text-lg", colors.textPrimary)}>
                            <Code className="w-5 h-5" />
                            {selectedTemplate ? 'Edit Template' : 'Template Editor'}
                          </CardTitle>
                          <CardDescription className={colors.textMuted}>
                            {selectedTemplate ? selectedTemplate.template_id : 'Select a template to edit'}
                          </CardDescription>
                        </div>
                        {selectedTemplate && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendTestTemplate(selectedTemplate.template_id)}
                              disabled={sendingTestTemplate === selectedTemplate.template_id}
                            >
                              {sendingTestTemplate === selectedTemplate.template_id ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              ) : (
                                <Send className="w-4 h-4 mr-1" />
                              )}
                              Send Test
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveTemplate}
                              disabled={savingTemplate}
                            >
                              {savingTemplate ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              ) : (
                                <Save className="w-4 h-4 mr-1" />
                              )}
                              Save
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {!selectedTemplate ? (
                        <div className={cn("flex flex-col items-center justify-center py-12", colors.textMuted)}>
                          <FileText className="w-12 h-12 mb-4 opacity-50" />
                          <p>Select a template from the list to edit</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Enabled Toggle */}
                          <div className="flex items-center justify-between">
                            <div>
                              <Label className={colors.textSecondary}>Enabled</Label>
                              <p className={cn("text-xs", colors.textMuted)}>
                                When disabled, this notification type will not send emails
                              </p>
                            </div>
                            <Switch
                              checked={editingTemplate?.enabled}
                              onCheckedChange={(checked) => handleTemplateChange('enabled', checked)}
                            />
                          </div>

                          {/* Template Name */}
                          <div>
                            <Label className={colors.textSecondary}>Display Name</Label>
                            <Input
                              value={editingTemplate?.name || ''}
                              onChange={(e) => handleTemplateChange('name', e.target.value)}
                              className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                            />
                          </div>

                          {/* Subject Line */}
                          <div>
                            <Label className={colors.textSecondary}>Subject Line</Label>
                            <Input
                              value={editingTemplate?.subject || ''}
                              onChange={(e) => handleTemplateChange('subject', e.target.value)}
                              placeholder="Email subject with {{variables}}"
                              className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                            />
                            <p className={cn("text-xs mt-1", colors.textMuted)}>
                              Use {'{{variable}}'} for dynamic content
                            </p>
                          </div>

                          {/* HTML Body */}
                          <div>
                            <Label className={colors.textSecondary}>HTML Body</Label>
                            <Textarea
                              value={editingTemplate?.html_body || ''}
                              onChange={(e) => handleTemplateChange('html_body', e.target.value)}
                              placeholder="<div>Email content with {{variables}}</div>"
                              rows={12}
                              className={cn("mt-1 font-mono text-sm", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                            />
                          </div>

                          {/* Available Variables */}
                          {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                            <div className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                              <p className={cn("text-xs font-medium mb-2", colors.textSecondary)}>
                                Available Variables:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {selectedTemplate.variables.map((v) => (
                                  <code 
                                    key={v} 
                                    className={cn("px-2 py-0.5 rounded text-xs", colors.bgPrimary, colors.textAccent)}
                                  >
                                    {`{{${v}}}`}
                                  </code>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>

      {/* Email Configuration Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowSettingsModal(false)} />
          <div className={cn("relative w-full max-w-lg rounded-xl shadow-2xl", colors.bgCard)}>
            <div className={cn("flex items-center justify-between p-4 border-b", colors.borderPrimary)}>
              <h3 className={cn("text-lg font-semibold flex items-center gap-2", colors.textPrimary)}>
                <Settings className="w-5 h-5" />
                Email Configuration
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowSettingsModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              {settingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                </div>
              ) : (
                <>
                  <div>
                    <Label className={colors.textSecondary}>Sender Email</Label>
                    <Input
                      value={settings.sender_email}
                      onChange={(e) => handleSettingChange('sender_email', e.target.value)}
                      placeholder="noreply@yourdomain.com"
                      className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                    />
                    <p className={cn("text-xs mt-1", colors.textMuted)}>
                      Must be verified in your Resend dashboard
                    </p>
                  </div>

                  <div>
                    <Label className={colors.textSecondary}>Sender Name</Label>
                    <Input
                      value={settings.sender_name}
                      onChange={(e) => handleSettingChange('sender_name', e.target.value)}
                      placeholder="StackRanked"
                      className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                    />
                  </div>

                  <div>
                    <Label className={colors.textSecondary}>Reply-To Email</Label>
                    <Input
                      value={settings.reply_to_email}
                      onChange={(e) => handleSettingChange('reply_to_email', e.target.value)}
                      placeholder="support@yourdomain.com"
                      className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                    />
                  </div>

                  <div>
                    <Label className={colors.textSecondary}>Email Footer</Label>
                    <Textarea
                      value={settings.email_footer}
                      onChange={(e) => handleSettingChange('email_footer', e.target.value)}
                      placeholder="Sent from StackRanked"
                      rows={2}
                      className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                    />
                  </div>
                </>
              )}
            </div>

            <div className={cn("flex justify-end gap-2 p-4 border-t", colors.borderPrimary)}>
              <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveSettings}
                disabled={saving || !hasChanges}
                className={cn(hasChanges ? colors.accentGradient : "")}
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  'Save Settings'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Details Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/60" onClick={() => setSelectedEmail(null)} />
          <div className={cn("relative w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-xl shadow-2xl", colors.bgCard)}>
            <div className={cn("sticky top-0 flex items-center justify-between p-4 border-b", colors.bgCard, colors.borderPrimary)}>
              <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Email Details</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {emailDetailsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={colors.textMuted}>From</Label>
                    <p className={cn("text-sm", colors.textPrimary)}>{selectedEmail.from}</p>
                  </div>
                  <div>
                    <Label className={colors.textMuted}>To</Label>
                    <p className={cn("text-sm", colors.textPrimary)}>
                      {Array.isArray(selectedEmail.to) ? selectedEmail.to.join(', ') : selectedEmail.to}
                    </p>
                  </div>
                  <div>
                    <Label className={colors.textMuted}>Subject</Label>
                    <p className={cn("text-sm", colors.textPrimary)}>{selectedEmail.subject}</p>
                  </div>
                  <div>
                    <Label className={colors.textMuted}>Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedEmail.last_event)}</div>
                  </div>
                  <div>
                    <Label className={colors.textMuted}>Sent At</Label>
                    <p className={cn("text-sm", colors.textPrimary)}>{formatDate(selectedEmail.created_at)}</p>
                  </div>
                  {selectedEmail.reply_to && (
                    <div>
                      <Label className={colors.textMuted}>Reply-To</Label>
                      <p className={cn("text-sm", colors.textPrimary)}>{selectedEmail.reply_to}</p>
                    </div>
                  )}
                </div>

                {selectedEmail.html && (
                  <div className="mt-6">
                    <Label className={colors.textMuted}>Email Content</Label>
                    <div 
                      className={cn("mt-2 p-4 rounded-lg border max-h-64 overflow-y-auto", colors.bgTertiary, colors.borderPrimary)}
                      dangerouslySetInnerHTML={{ __html: selectedEmail.html }}
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => handleResendEmail(selectedEmail.id)}
                    disabled={resendingId === selectedEmail.id}
                  >
                    {resendingId === selectedEmail.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resending...</>
                    ) : (
                      <><RotateCcw className="w-4 h-4 mr-2" />Resend Email</>
                    )}
                  </Button>
                  <Button onClick={() => setSelectedEmail(null)}>Close</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailSettingsPage;
